import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { BadgePercent, MapPin, Loader2, Store, ArrowLeft, Tag, Network, Heart, Star, HelpCircle } from "lucide-react";
import HostPromosSection from "../components/HostPromosSection";
import IntroTour from "../components/IntroTour";
import { PRO_CATEGORY_ORDER, catMeta, catLabel } from "../data/proCategories";

const SCONTI_TOUR_STEPS = [
  { icon: BadgePercent, title: "Perché offrire sconti?", text: "Gli sconti danno all'ospite un motivo concreto per aprire e tornare sul tuo Welcome Book durante tutto il soggiorno, non solo il primo giorno." },
  { icon: Heart, title: "Trattieni e fidelizza", text: "Un ospite che trova offerte utili resta \"agganciato\" al Welcome Book: lo consulta più spesso, si sente coccolato e vive un'esperienza migliore." },
  { icon: Star, title: "Più recensioni e ospiti di ritorno", text: "Un soggiorno percepito come più ricco genera più recensioni positive e aumenta le probabilità che l'ospite torni o ti consigli ad amici." },
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

const BoxHeader = ({ icon: Icon, title, subtitle, accent }) => (
  <div className="flex items-start gap-3 mb-4">
    <div className="shrink-0 w-11 h-11 rounded-2xl flex items-center justify-center text-white font-black shadow-sm" style={{ background: accent }}>
      <Icon className="w-5 h-5" />
    </div>
    <div className="min-w-0">
      <h2 className="text-base sm:text-lg font-black text-zinc-900 leading-tight">{title}</h2>
      <p className="text-xs text-zinc-500 mt-0.5">{subtitle}</p>
    </div>
  </div>
);

export default function ProHostSection() {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const [radius, setRadius] = useState(10);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tourNonce, setTourNonce] = useState(0);

  const load = useCallback(async (r) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/pro/host/${propertyId}/nearby?radius=${r}`, auth());
      setData(res.data);
      if (res.data.radius) setRadius(res.data.radius);
    } catch (e) { setData({ error: true }); }
    finally { setLoading(false); }
  }, [propertyId]);

  useEffect(() => { load(10); }, [load]);

  const toggle = async (promo_id, enable) => {
    setData((d) => ({ ...d, promos: d.promos.map((p) => p.promo_id === promo_id ? { ...p, active: enable } : p) }));
    try { await axios.post(`${API}/pro/host/${propertyId}/toggle`, { promo_id, enable }, auth()); }
    catch { load(radius); }
  };

  return (
    <div className="min-h-screen bg-zinc-50 pb-20" data-testid="pro-host-section">
      <IntroTour key={tourNonce} storageKey={`sconti_tour_${propertyId}`} steps={SCONTI_TOUR_STEPS} accent="#7C3AED" forceShow={tourNonce > 0} />
      {/* Hero */}
      <header className="text-white px-4 pt-6 pb-10" style={{ background: "linear-gradient(135deg,#d04260 0%,#e11d48 55%,#7C3AED 130%)" }}>
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-3">
            <button onClick={() => navigate(-1)} className="text-white/85 text-sm inline-flex items-center gap-1 hover:text-white transition-colors" data-testid="pro-host-back"><ArrowLeft className="w-4 h-4" /> Torna all'editor</button>
            <button onClick={() => setTourNonce((n) => n + 1)} className="text-white/90 text-xs font-bold inline-flex items-center gap-1.5 bg-white/15 hover:bg-white/25 transition-colors px-3 py-1.5 rounded-full" data-testid="sconti-tour-replay">
              <HelpCircle className="w-4 h-4" /> Perché offrire sconti?
            </button>
          </div>
          <div className="flex items-center gap-2 mb-1">
            <BadgePercent className="w-7 h-7" />
            <h1 className="text-2xl sm:text-3xl font-black tracking-tight">Sconti &amp; Promozioni</h1>
          </div>
          <p className="text-white/85 text-sm max-w-xl">Tutto ciò che i tuoi ospiti vedono nel Welcome Book: gli sconti dei partner Hostland, le tue offerte personalizzate e le attività reali della tua zona con la rete Hostland Pro.</p>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 -mt-6">
        <div className="grid lg:grid-cols-3 gap-5 items-start">

        {/* ── BOX 1: Rete Hostland Pro ── */}
        <motion.section initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-3xl border-2 p-5 relative overflow-hidden min-w-0" style={{ borderColor: "#7C3AED", background: "linear-gradient(160deg,#faf5ff, #ffffff 60%)" }} data-testid="pro-block-network">
          <span className="absolute top-4 right-4 text-[9px] font-black bg-violet-600 text-white px-2 py-0.5 rounded-full tracking-wider">NOVITÀ</span>
          <BoxHeader icon={Network} accent="linear-gradient(135deg,#7C3AED,#EC4899)"
            title="Rete Hostland Pro"
            subtitle="Attività reali del territorio abbonate a Hostland Pro vicino a te. Attivale con un tap." />

          {/* Città + raggio */}
          <div className="flex flex-wrap items-center gap-2 mb-5 bg-white/70 rounded-2xl border border-violet-100 px-3 py-2.5">
            <span className="text-xs font-black text-zinc-500 uppercase tracking-wider">Raggio</span>
            {[10, 20].map((r) => (
              <button key={r} onClick={() => load(r)} className={`px-4 py-1.5 rounded-full text-sm font-bold transition-colors ${radius === r ? "bg-violet-600 text-white shadow" : "bg-white border border-zinc-200 text-zinc-600"}`} data-testid={`pro-radius-${r}`}>{r} km</button>
            ))}
            {data?.ref_city && <span className="text-xs text-violet-700 font-bold ml-auto flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {data.ref_city}</span>}
          </div>

          {loading ? (
            <div className="py-14 text-center"><Loader2 className="w-6 h-6 animate-spin text-violet-400 mx-auto" /></div>
          ) : data?.needs_city ? (
            <div className="rounded-2xl bg-amber-50 border border-amber-200 p-5 text-center text-sm text-amber-800" data-testid="pro-needs-city">
              Imposta la <strong>città</strong> della struttura (nell'editor della struttura) per vedere le attività Pro vicine. È la stessa città usata per il meteo.
            </div>
          ) : data?.error ? (
            <div className="rounded-2xl bg-zinc-50 border border-zinc-200 p-6 text-center text-sm text-zinc-400">Impossibile caricare le attività della rete. Riprova.</div>
          ) : data?.promos?.length === 0 ? (
            <div className="rounded-2xl bg-white border border-zinc-100 p-8 text-center text-sm text-zinc-400" data-testid="pro-network-empty">
              Nessuna attività Hostland Pro nel raggio selezionato per ora.<br />Torna a controllare: la rete cresce ogni settimana.
            </div>
          ) : (
            <div className="space-y-6">
              {PRO_CATEGORY_ORDER.map((cat) => {
                const rank = (p) => (p.boost === "super_power" ? 2 : p.boost === "power" ? 1 : 0);
                const items = data.promos
                  .filter((p) => (p.category || "Altro") === cat)
                  .sort((a, b) => rank(b) - rank(a) || String(b.published_at || "").localeCompare(String(a.published_at || "")));
                if (!items.length) return null;
                const meta = catMeta(cat);
                const CatIcon = meta.icon;
                return (
                  <div key={cat} data-testid={`pro-cat-group-${cat}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${meta.color}1a`, color: meta.color }}>
                        <CatIcon className="w-4 h-4" strokeWidth={2.2} />
                      </span>
                      <p className="text-[13px] font-black uppercase tracking-wide text-zinc-700">{catLabel(cat, "it")}</p>
                      <span className="text-[11px] font-bold text-zinc-400">· {items.length}</span>
                    </div>
                    <div className="space-y-3">
                      {items.map((p, i) => (
                        <motion.div key={p.promo_id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                          className="rounded-2xl bg-white border-2 p-4 transition-colors" style={{ borderColor: p.active ? "#7C3AED" : "#f4f4f5" }} data-testid={`pro-host-card-${p.promo_id}`}>
                          <div className="flex items-start gap-3 mb-3">
                            <div className="w-12 h-12 rounded-2xl bg-zinc-100 overflow-hidden shrink-0 flex items-center justify-center">
                              {p.logo_url ? <img src={p.logo_url.startsWith("http") ? p.logo_url : `${process.env.REACT_APP_BACKEND_URL}${p.logo_url}`} alt="" className="w-full h-full object-cover" /> : <Store className="w-6 h-6 text-zinc-300" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-black leading-tight truncate">{p.business_name}</p>
                              <p className="text-[11px] text-zinc-400 font-semibold">{p.category} · {p.distance_km} km</p>
                              {p.pill && <span className="inline-block mt-1 text-[11px] font-black bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full">{p.pill}</span>}
                            </div>
                          </div>
                          {p.promo_desc && <p className="text-xs text-zinc-500 mb-3 line-clamp-2">{p.promo_desc}</p>}
                          <button onClick={() => toggle(p.promo_id, !p.active)} className={`w-full py-2 rounded-full text-sm font-bold transition-colors inline-flex items-center justify-center gap-1.5 ${p.active ? "bg-violet-600 text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"}`} data-testid={`pro-host-toggle-${p.promo_id}`}>
                            {p.active ? "✓ Attiva nel Welcome Book" : "Attiva"}
                          </button>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </motion.section>

        {/* ── BOX 2: Le tue promo locali ── */}
        <motion.section initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.06 }}
          className="rounded-3xl bg-white border border-zinc-100 shadow-sm p-5 min-w-0 overflow-hidden" data-testid="pro-block-local">
          <BoxHeader icon={Tag} accent="linear-gradient(135deg,#f43f5e,#e11d48)"
            title="Le tue promo locali"
            subtitle="Crea gli sconti dei tuoi partner locali: musei, ristoranti, noleggi…" />
          <HostPromosSection propertyId={propertyId} token={token} variant="local" />
        </motion.section>

        {/* ── BOX 3: Sconti Hostland interni ── */}
        <motion.section initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}
          className="rounded-3xl bg-white border border-zinc-100 shadow-sm p-5 min-w-0 overflow-hidden" data-testid="pro-block-hostland">
          <BoxHeader icon={BadgePercent} accent="linear-gradient(135deg,#f59e0b,#ef4444)"
            title="Sconti Hostland"
            subtitle="Gli sconti dei partner di Vita da Host, già attivi: disattiva quelli non presenti in zona." />
          <HostPromosSection propertyId={propertyId} token={token} variant="hostland" />
        </motion.section>

        </div>
      </main>
    </div>
  );
}
